import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { GraduationCap, Plus, Clock, CheckCircle, XCircle, Calendar, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_ABBR = { Saturday: 'Sat', Sunday: 'Sun', Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri' };
const formatProgramSchedule = (p) => {
    const days = (p.days || []).map(d => DAY_ABBR[d] || d).join(' & ');
    const time = p.start_time && p.end_time ? `${p.start_time} to ${p.end_time}` : '';
    if (!days && !time) return '';
    return ` - ${[days, time].filter(Boolean).join(' - ')}`;
};

const Children = () => {
    const { user } = useAuth();
    const [children, setChildren] = useState([]);
    const [waitingList, setWaitingList] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [logoUrl, setLogoUrl] = useState(null);
    const [parentAddress, setParentAddress] = useState('');
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [addressChoice, setAddressChoice] = useState('parent'); // 'parent' | 'other'
    const [otherAddress, setOtherAddress] = useState('');
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        arabic_name: '',
        date_of_birth: '',
        gender: '',
        branch: '',
        program_id: ''
    });

    useEffect(() => {
        fetchData();
    }, [user]);

    const fetchData = async () => {
        try {
            const effectiveId = user?.linked_parent_id || user?.id;
            const [activeRes, waitingRes, programsRes, settingsRes, parentRes] = await Promise.all([
                axios.get(`${API_URL}/api/students?status=active&parent_id=${effectiveId}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/students?status=waiting_list&parent_id=${effectiveId}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                axios.get(`${API_URL}/api/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
                axios.get(`${API_URL}/api/parents/${effectiveId}`, { withCredentials: true }).catch(() => ({ data: {} })),
            ]);
            setChildren(activeRes.data);
            setWaitingList(waitingRes.data);
            setPrograms(programsRes.data);
            setLogoUrl(settingsRes.data?.logo_url || null);
            setParentAddress(parentRes.data?.address || '');
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Ages helper — years between two ISO dates
    const ageInYears = (dob) => {
        if (!dob) return null;
        const d = new Date(dob);
        if (isNaN(d)) return null;
        const now = new Date();
        let a = now.getFullYear() - d.getFullYear();
        const m = now.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
        return a;
    };

    const branches = React.useMemo(() => {
        return Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort();
    }, [programs]);

    const eligiblePrograms = React.useMemo(() => {
        if (!formData.date_of_birth || !formData.branch) return [];
        const age = ageInYears(formData.date_of_birth);
        const g = (formData.gender || '').toLowerCase();
        return programs.filter(p => {
            if (p.location !== formData.branch) return false;
            if (p.min_age != null && age < p.min_age) return false;
            if (p.max_age != null && age > p.max_age) return false;
            const pg = (p.gender_eligibility || 'both').toLowerCase();
            if (g && pg !== 'both' && pg !== g) return false;
            return true;
        });
    }, [programs, formData.branch, formData.date_of_birth, formData.gender]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.date_of_birth) {
            toast.error('Please fill the date of birth first');
            return;
        }
        if (!formData.branch) {
            toast.error('Please choose a branch');
            return;
        }
        if (!formData.program_id) {
            toast.error('Please choose a program');
            return;
        }
        setSaving(true);
        try {
            const finalAddress = addressChoice === 'parent' ? parentAddress : otherAddress;
            await axios.post(`${API_URL}/api/students`, {
                first_name: formData.first_name,
                last_name: formData.last_name,
                arabic_name: formData.arabic_name,
                date_of_birth: formData.date_of_birth,
                gender: formData.gender,
                address: finalAddress,
                program_id: formData.program_id,
                parent_id: user.linked_parent_id || user.id
            }, { withCredentials: true });
            toast.success('Child added to waiting list successfully');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add child');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: '', last_name: '', arabic_name: '', date_of_birth: '',
            gender: '', branch: '', program_id: ''
        });
        setAddressChoice('parent');
        setOtherAddress('');
    };

    const getProgramName = (id) => programs.find(p => p.id === id)?.name || 'N/A';

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="children-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">My Students</h1>
                        <p className="text-[#4B5563]">Manage your enrolled students</p>
                    </div>
                    <Button onClick={() => setDialogOpen(true)} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-child-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Child to Waiting List
                    </Button>
                </div>

                {/* Active Children */}
                <div>
                    <h2 className="text-lg font-semibold text-[#1F2937] mb-4 flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2 text-[#10B981]" />
                        Enrolled Children ({children.length})
                    </h2>
                    {children.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {children.map((child) => (
                                <Card key={child.id} className="card-surface">
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="h-12 w-12 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-bold">
                                                {child.first_name?.[0]}{child.last_name?.[0]}
                                            </div>
                                            <Badge className="bg-[#10B981]">Active</Badge>
                                        </div>
                                        <h3 className="text-lg font-semibold text-[#1F2937]">{child.first_name} {child.last_name}</h3>
                                        <p className="text-sm text-[#4B5563] mb-3">{child.arabic_name}</p>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-[#4B5563]">Reg Code</span>
                                                <span className="font-mono font-medium">{child.reg_code}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#4B5563]">Program</span>
                                                <span className="font-medium">{getProgramName(child.program_id)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#4B5563]">Gender</span>
                                                <span className="font-medium capitalize">{child.gender}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#4B5563]">DOB</span>
                                                <span className="font-medium tabular-nums">{child.date_of_birth}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card className="card-surface">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <GraduationCap className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563]">No enrolled children yet</p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Waiting List */}
                <div>
                    <h2 className="text-lg font-semibold text-[#1F2937] mb-4 flex items-center">
                        <Clock className="h-5 w-5 mr-2 text-[#F59E0B]" />
                        Waiting List ({waitingList.length})
                    </h2>
                    {waitingList.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {waitingList.map((child) => (
                                <Card key={child.id} className="card-surface border-l-4 border-l-[#F59E0B]">
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="h-12 w-12 rounded-full bg-[#F59E0B]/20 flex items-center justify-center text-[#F59E0B] font-bold">
                                                {child.first_name?.[0]}{child.last_name?.[0]}
                                            </div>
                                            <Badge className="bg-[#F59E0B]">Pending</Badge>
                                        </div>
                                        <h3 className="text-lg font-semibold text-[#1F2937]">{child.first_name} {child.last_name}</h3>
                                        <p className="text-sm text-[#4B5563] mb-3">{child.arabic_name}</p>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-[#4B5563]">Reg Code</span>
                                                <span className="font-mono font-medium">{child.reg_code}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#4B5563]">Desired Program</span>
                                                <span className="font-medium">{getProgramName(child.program_id)}</span>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-xs text-[#4B5563] bg-[#FAF9F6] p-2 rounded">
                                            Your application is being reviewed. You will be notified once approved.
                                        </p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card className="card-surface">
                            <CardContent className="flex flex-col items-center justify-center py-8">
                                <p className="text-[#4B5563]">No pending applications</p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Add Child Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <div className="flex items-center gap-3">
                                {logoUrl && <img src={logoUrl} alt="School Logo" className="w-10 h-10 rounded-lg object-contain" />}
                                <DialogTitle className="font-['Outfit']">Add Child to Waiting List</DialogTitle>
                            </div>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>First Name</Label>
                                    <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Last Name</Label>
                                    <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Arabic Name (optional)</Label>
                                <Input value={formData.arabic_name} onChange={(e) => setFormData({...formData, arabic_name: e.target.value})} dir="rtl" data-testid="child-arabic-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Date of Birth</Label>
                                    <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Gender</Label>
                                    <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                                        <SelectTrigger data-testid="child-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="male">Male</SelectItem>
                                            <SelectItem value="female">Female</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Address (Which parent does child live with?)</Label>
                                <Select value={addressChoice} onValueChange={(v) => setAddressChoice(v)}>
                                    <SelectTrigger data-testid="child-address-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="parent">{parentAddress ? `Same as guardian — ${parentAddress}` : 'Same as guardian'}</SelectItem>
                                        <SelectItem value="other">Other address</SelectItem>
                                    </SelectContent>
                                </Select>
                                {addressChoice === 'other' && (
                                    <Input
                                        value={otherAddress}
                                        onChange={(e) => setOtherAddress(e.target.value)}
                                        placeholder="Type the child's address"
                                        required
                                        data-testid="child-other-address"
                                    />
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label>Branch *</Label>
                                <Select value={formData.branch} onValueChange={(v) => setFormData({...formData, branch: v, program_id: ''})}>
                                    <SelectTrigger data-testid="child-branch-select"><SelectValue placeholder="Select a branch first" /></SelectTrigger>
                                    <SelectContent>
                                        {branches.length === 0 && <SelectItem value="__none__" disabled>No branches available</SelectItem>}
                                        {branches.map(b => (
                                            <SelectItem key={b} value={b}>{b}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Desired Program *</Label>
                                {!formData.date_of_birth ? (
                                    <div className="text-xs text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] rounded p-2" data-testid="dob-first-hint">Please fill the date of birth first — programs are age-restricted.</div>
                                ) : !formData.gender ? (
                                    <div className="text-xs text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] rounded p-2" data-testid="gender-first-hint">Please pick the child&apos;s gender — some programs are gender-specific.</div>
                                ) : !formData.branch ? (
                                    <div className="text-xs text-[#4B5563] bg-[#F3F4F6] border border-[#E5E7EB] rounded p-2" data-testid="branch-first-hint">Choose a branch to see its programs.</div>
                                ) : eligiblePrograms.length === 0 ? (
                                    <div className="text-xs text-[#991B1B] bg-[#FEF2F2] border border-[#FCA5A5] rounded p-2" data-testid="no-eligible-programs">No programs in this branch match the child&apos;s age and gender.</div>
                                ) : (
                                    <Select value={formData.program_id} onValueChange={(v) => setFormData({...formData, program_id: v})}>
                                        <SelectTrigger data-testid="child-program-select"><SelectValue placeholder="Select program" /></SelectTrigger>
                                        <SelectContent>
                                            {eligiblePrograms.map(p => (
                                                <SelectItem key={p.id} value={p.id}>{p.name}{formatProgramSchedule(p)}{p.min_age != null && p.max_age != null ? ` (${p.min_age}-${p.max_age}y)` : ''}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Submit Application
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Children;
